import { create } from 'zustand';
import preferencesService, { UserPreferences, UserPreferencesUpdate } from '@/services/preferences';
import { useThemeStore } from '@/stores/themeStore';
import { useAuthStore } from '@/stores/authStore';
import { logger } from '@/utils/logger';

interface PreferencesState {
  preferences: UserPreferences | null;
  isLoading: boolean;
  error: string | null;
  isSaving: boolean;

  // Actions
  loadPreferences: () => Promise<void>;
  updatePreferences: (updates: UserPreferencesUpdate) => Promise<void>;
  resetPreferences: () => Promise<void>;
  setPreference: <K extends keyof UserPreferencesUpdate>(
    key: K,
    value: UserPreferencesUpdate[K]
  ) => void;
}

// Debounce helper
let saveTimeout: NodeJS.Timeout | null = null;
const DEBOUNCE_DELAY = 1000; // 1 second

export const usePreferencesStore = create<PreferencesState>((set, get) => ({
  preferences: null,
  isLoading: false,
  error: null,
  isSaving: false,

  loadPreferences: async () => {
    set({ isLoading: true, error: null });
    try {
      const preferences = await preferencesService.getPreferences();
      set({ preferences, isLoading: false });

      // Sync theme to themeStore (single source of truth for theme)
      useThemeStore.getState().setTheme(preferences.theme);

      // Store in localStorage for quick access (excluding theme which is managed by themeStore)
      localStorage.setItem('userPreferences', JSON.stringify(preferences));
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to load preferences';
      set({ error: errorMessage, isLoading: false });
      logger.error('Failed to load preferences:', error);
    }
  },

  updatePreferences: async (updates: UserPreferencesUpdate) => {
    const currentPreferences = get().preferences;
    if (!currentPreferences) return;

    // Optimistic update
    const optimisticPreferences = {
      ...currentPreferences,
      ...updates,
    };
    set({ preferences: optimisticPreferences });

    // Sync theme to themeStore if it was updated (single source of truth)
    if (updates.theme) {
      useThemeStore.getState().setTheme(updates.theme);
    }

    // Update localStorage immediately
    localStorage.setItem('userPreferences', JSON.stringify(optimisticPreferences));

    // Debounced save to backend
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      saveTimeout = null;
    }

    saveTimeout = setTimeout(async () => {
      // Skip save if user is no longer authenticated
      if (!useAuthStore.getState().isAuthenticated) {
        saveTimeout = null;
        return;
      }

      set({ isSaving: true });
      try {
        const updatedPreferences = await preferencesService.partialUpdatePreferences(updates);
        set({ preferences: updatedPreferences, isSaving: false });
        localStorage.setItem('userPreferences', JSON.stringify(updatedPreferences));
      } catch (error: any) {
        const errorMessage = error.response?.data?.detail || 'Failed to save preferences';
        set({ error: errorMessage, isSaving: false });
        logger.error('Failed to save preferences:', error);

        // Revert optimistic update on error
        set({ preferences: currentPreferences });
        localStorage.setItem('userPreferences', JSON.stringify(currentPreferences));

        // Revert theme if it failed
        if (updates.theme && currentPreferences.theme) {
          useThemeStore.getState().setTheme(currentPreferences.theme);
        }
      }
      saveTimeout = null;
    }, DEBOUNCE_DELAY);
  },

  resetPreferences: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await preferencesService.resetPreferences();
      set({ preferences: response.preferences, isLoading: false });

      // Sync default theme to themeStore (single source of truth)
      useThemeStore.getState().setTheme(response.preferences.theme);

      // Update localStorage
      localStorage.setItem('userPreferences', JSON.stringify(response.preferences));
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to reset preferences';
      set({ error: errorMessage, isLoading: false });
      logger.error('Failed to reset preferences:', error);
    }
  },

  setPreference: <K extends keyof UserPreferencesUpdate>(
    key: K,
    value: UserPreferencesUpdate[K]
  ) => {
    const updates = { [key]: value } as UserPreferencesUpdate;
    get().updatePreferences(updates);
  },
}));

// Cleanup function to remove pending timeouts
// Note: Theme-related media query listeners are managed by themeStore
export const cleanupPreferencesStore = () => {
  // Clear any pending save timeout
  if (saveTimeout) {
    clearTimeout(saveTimeout);
    saveTimeout = null;
  }
};
